import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {useLocation} from 'react-router-dom';

export type MobileTab =
  | 'home'
  | 'communities'
  | 'messages'
  | 'notifications'
  | 'more';

type MobileNavigationContextType = {
  activeTab: MobileTab;
  isMoreSheetOpen: boolean;
  openMoreSheet: () => void;
  closeMoreSheet: () => void;
};

const MobileNavigationContext =
  createContext<MobileNavigationContextType | null>(null);

export const useMobileNavigation = () => {
  const context = useContext(MobileNavigationContext);
  if (!context) {
    throw new Error(
      'useMobileNavigation must be used within a MobileNavigationProvider',
    );
  }
  return context;
};

export const useMobileNavigationSafe = () => {
  return useContext(MobileNavigationContext);
};

const TAB_PATH_MAP: Record<string, MobileTab> = {
  '/dashboard': 'home',
  '/communities': 'communities',
  '/homes': 'communities',
  '/groups': 'communities',
  '/pulse': 'communities',
  '/contact-profiles': 'communities',
  '/verification': 'communities',
  '/contractors': 'communities',
  '/blocks': 'communities',
  '/messages': 'messages',
  '/notifications': 'notifications',
};

const getTabFromPath = (pathname: string): MobileTab => {
  for (const [path, tab] of Object.entries(TAB_PATH_MAP)) {
    if (pathname.startsWith(path)) return tab;
  }
  if (pathname === '/') return 'home';
  return 'more';
};

export const TAB_TITLE_MAP: Record<MobileTab, string> = {
  home: 'Home',
  communities: 'Communities',
  messages: 'Messages',
  notifications: 'Notifications',
  more: 'More',
};

type MobileNavigationProviderProps = {
  children: ReactNode;
};

export const MobileNavigationProvider = ({
  children,
}: MobileNavigationProviderProps) => {
  const {pathname} = useLocation();
  const activeTab = getTabFromPath(pathname);
  const [isMoreSheetOpen, setIsMoreSheetOpen] = useState(false);

  const openMoreSheet = useCallback(() => setIsMoreSheetOpen(true), []);
  const closeMoreSheet = useCallback(() => setIsMoreSheetOpen(false), []);

  const value = useMemo(
    () => ({
      activeTab,
      isMoreSheetOpen,
      openMoreSheet,
      closeMoreSheet,
    }),
    [activeTab, isMoreSheetOpen, openMoreSheet, closeMoreSheet],
  );

  return (
    <MobileNavigationContext.Provider value={value}>
      {children}
    </MobileNavigationContext.Provider>
  );
};
